import PropTypes from 'prop-types';
import {createUseStyles} from 'react-jss';
import {connect} from 'react-redux';
import Language from '../../../v2/lang/language';
import Translation from '../../../v2/lang/Translation';
import makeGetDeliveryOptionAdditionalAdvice
from '../../../v2/selectors/DeliveryOptionsSelectors/makeGetDeliveryOptionAdditionalAdvice';
import makeGetShouldHideDeliveryEstimate
from '../../../v2/selectors/DeliveryOptionsSelectors/makeGetShouldHideDeliveryEstimate';
import makeGetDeliveryOptionType from '../../../v2/selectors/DeliveryOptionsSelectors/makeGetDeliveryOptionType';
import makeGetShouldHideStoreInfo from '../../../v2/selectors/DeliveryOptionsSelectors/makeGetShouldHideStoreInfo';
import {getSiteCode} from '../../../v2/selectors/selectors';
import Spacing from '../../../v2/style-helper/Spacing';
import DeliveryOptionEstimate from '../DeliveryOptionEstimate/DeliveryOptionEstimate';
import DeliveryOptionPrice from '../DeliveryOptionPrice/DeliveryOptionPrice';
import DeliveryOptionPricePropType from '../../../propTypes/DeliveryOptionPrice.proptype';
import DeliveryOptionStoreInformation from '../DeliveryOptioStoreInformation/DeliveryOptionStoreInformation';
import StoreInfoPropType from '../DeliveryOptioStoreInformation/storeInfo.proptype';

const useStyles = createUseStyles({
  info: {
    margin: [Spacing.double, 0, Spacing.single, 0],
    color: '#666',
  },
  storeInfoLine: {
    marginBottom: Spacing.single,
  },
  priceLine: {
    marginBottom: Spacing.single,
    marginTop: Spacing.single,
  },
  fastTrack: {
    fontWeight: 500,
    color: '#168ab3',
  },
});

export function DeliveryOptionShipmentContents(props) {
  const classes = useStyles(props);
  
  const priceLine = (
    <DeliveryOptionPrice
      taxable={props.taxable}
      price={props.deliveryOption.price}
      discountedPrice={props.deliveryOption.discountedPrice}
      taxPrice={props.deliveryOption.taxPrice}
      isOptionalGroup={props.isOptionalGroup}
      groupId={props.groupId}
    />
  );
  
  return (
    <div
      className="DeliveryOptionShipmentContents"
      id={`DeliveryOptionShipmentContents-${props.groupId}-${props.index}`}
    >
      {!props.shouldHideStoreInfo && props.deliveryOption.store && (
        <div className={classes.storeInfoLine} data-test="DeliveryOptionShipmentContents__storeInfo">
          <DeliveryOptionStoreInformation store={props.deliveryOption.store} />
        </div>
      )}
      
      {!props.shouldHideDeliveryEstimate && (
        <DeliveryOptionEstimate
          endDate={props.deliveryOption.endDate}
          min={props.min}
          max={props.max}
          estimate={props.estimate}
          deliveryOptionType={props.deliveryOptionType}
          deliveryEstimate={props.deliveryEstimate}
          siteCode={props.siteCode}
        />
      )}
      
      {!props.hasNamedDayOptions && (
        <div className={classes.priceLine} data-test="DeliveryOptionShipmentContents__priceLine">
          {props.hasToggle ? (
            <span className={classes.fastTrack} data-test="DeliveryOptionShipmentContents__fastTrackAppliesMessage">
              <Translation placeholderValues={[priceLine]}>
                {props.priceAppliesMessage}
              </Translation>
            </span>
          ) : priceLine}
        </div>
      )}
      
      {props.hasInfoText && (
        <div className={classes.info} data-test="DeliveryOptionShipmentContents__info">
          <Translation optionalExtension={props.siteCode}>{props.info}</Translation>
        </div>
      )}
      
      {props.additionalAdvice && (
        <div className={classes.info} data-test="DeliveryOptionShipmentContents__additionalAdvice">
          <Translation>{props.additionalAdvice}</Translation>
        </div>
      )}
    </div>
  );
}

DeliveryOptionShipmentContents.propTypes = {
  info: PropTypes.string.isRequired,
  additionalAdvice: PropTypes.string.isRequired,
  min: PropTypes.string.isRequired,
  max: PropTypes.string.isRequired,
  hasToggle: PropTypes.bool.isRequired,
  taxable: PropTypes.bool.isRequired,
  hasInfoText: PropTypes.bool.isRequired,
  isOptionalGroup: PropTypes.bool.isRequired,
  priceAppliesMessage: PropTypes.string.isRequired,
  estimate: PropTypes.string.isRequired,
  deliveryEstimate: PropTypes.string.isRequired,
  deliveryOptionType: PropTypes.string.isRequired,
  groupId: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  deliveryOption: PropTypes.shape({
    deliveryStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deliveryEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.number,
    type: PropTypes.string.isRequired,
    translationKeys: PropTypes.number,
    price: DeliveryOptionPricePropType,
    discountedPrice: DeliveryOptionPricePropType,
    taxPrice: DeliveryOptionPricePropType,
    minimumDeliveryDays: PropTypes.number,
    maximumDeliveryDays: PropTypes.number,
    taxable: PropTypes.bool,
    fastDelivery: PropTypes.bool,
    residentIdRequired: PropTypes.bool,
    loyaltyOfferType: PropTypes.object,
    store: PropTypes.shape(StoreInfoPropType),
  }).isRequired,
  hasNamedDayOptions: PropTypes.bool.isRequired,
  shouldHideDeliveryEstimate: PropTypes.bool,
  shouldHideStoreInfo: PropTypes.bool,
  siteCode: PropTypes.string,
};

const mapStateToProps = (state, ownProps) => {
  const getAdditionalAdvice = makeGetDeliveryOptionAdditionalAdvice(ownProps.deliveryOption.id);
  
  const info = makeGetDeliveryOptionType(
    ownProps.deliveryOption.translationKeys.info, ownProps.deliveryOption.id, 'info',
  )(state);
  
  const siteCode = getSiteCode(state);
  
  const infoWithSiteCode = `${info}.${siteCode}`;
  
  const priceAppliesMessage = `fastDelivery.applies.to.items.${ownProps.fastTrackProducts.length > 1
    ? 'plural'
    : 'singular'}`;
  
  const getShouldHideDeliveryEstimate = makeGetShouldHideDeliveryEstimate(ownProps.deliveryOption.id);
  const getShouldHideStoreInfo = makeGetShouldHideStoreInfo(ownProps.deliveryOption.id);
  
  return {
    priceAppliesMessage,
    info,
    siteCode,
    additionalAdvice: getAdditionalAdvice(state),
    shouldHideDeliveryEstimate: getShouldHideDeliveryEstimate(state),
    shouldHideStoreInfo: getShouldHideStoreInfo(state),
    hasInfoText: Language.doesTranslationExist(info) || Language.doesTranslationExist(infoWithSiteCode),
    estimate: ownProps.deliveryOption.translationKeys.estimate,
    min: ownProps.deliveryOption.minimumDeliveryDays,
    max: ownProps.deliveryOption.maximumDeliveryDays,
  };
};

export default connect(mapStateToProps)(DeliveryOptionShipmentContents);
